// src/api/createTransaction.ts
var createTransaction = (data) => {
  return {
    success: true,
    transactionId: "mocked-tx-001",
    data
  };
};

// src/utils/axios.ts
import axios from "axios";
import { jwtDecode } from "jwt-decode";
var apiBaseUrl = "";
var axiosInstance = null;
var getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};
var isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    console.log("\u23F0 Token expires at:", new Date(decoded.exp * 1e3).toISOString());
    console.log("\u{1F552} Current time:", (/* @__PURE__ */ new Date()).toISOString());
    return decoded.exp * 1e3 < Date.now();
  } catch (error) {
    console.error("\u274C Error decoding token:", error);
    return true;
  }
};
var setApiBaseUrl = (url) => {
  apiBaseUrl = url;
  axiosInstance = createAxiosInstance();
};
var createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: apiBaseUrl,
    headers: {
      "Content-Type": "application/json"
    },
    withCredentials: true
    // ⚠️ Keep this if your backend expects cookies on the request
  });
  instance.interceptors.request.use((config) => {
    const base = config.baseURL ?? apiBaseUrl;
    if (typeof base === "string" && /\/api\/v1\/?$/.test(base) && typeof config.url === "string" && config.url.startsWith("/api/v1/")) {
      config.url = config.url.replace(/^\/api\/v1/, "");
    }
    const token = getCookie("idToken");
    console.log("\u{1F510} Token from cookies:", token);
    if (token && !isTokenExpired(token)) {
      config.headers["Authorization"] = `Bearer ${token}`;
      console.log("\u2705 Token attached to request.");
    } else {
      console.warn("\u26A0\uFE0F No valid token found. Request may fail.");
    }
    return config;
  });
  return instance;
};
var getAxios = () => {
  if (!axiosInstance) {
    const windowApi = typeof window !== "undefined" ? window.__COREMIND_API_URL__ : "";
    if (windowApi) {
      setApiBaseUrl(windowApi);
    }
  }
  if (!axiosInstance) {
    throw new Error("Axios instance not initialized. Call setApiBaseUrl() first.");
  }
  return axiosInstance;
};

// src/hooks/useCreateTransaction.ts
var useCreateTransaction = () => {
  const create = async (payload) => {
    const maxRetries = 3;
    let attempts = 0;
    while (attempts < maxRetries) {
      try {
        const res = await getAxios().post("/api/v1/payments/transactions", payload);
        return res.data;
      } catch (err) {
        attempts++;
        if (attempts >= maxRetries) {
          throw new Error(`Failed after ${maxRetries} retries: ${err.message}`);
        }
      }
    }
  };
  return { create };
};

// src/hooks/useCreateCart.ts
import { v4 as uuidv4 } from "uuid";
var useCreateCart = () => {
  const addItem = async (userId, accountId, item) => {
    const payload = {
      item: {
        name: uuidv4(),
        user_id: userId,
        account_id: accountId,
        product_id: item.productId,
        description: item.description,
        amount: item.amount,
        quantity: item.quantity,
        tax_rate: item.taxRate || "0.0000",
        tax_amount: item.taxAmount || "0.00",
        is_taxable: item.isTaxable ?? false
      },
      link: {
        source_type: "product",
        // Or however you're defining it
        source_id: item.productId
        // Usually this is productId or listingId
      }
    };
    const res = await getAxios().post("/api/v1/payments/cart/add", payload);
    return res.data;
  };
  const addItems = async (userId, accountId, cartItems) => {
    for (const item of cartItems) {
      await addItem(userId, accountId, item);
    }
  };
  return { addItem, addItems };
};

// src/hooks/useCartItems.ts
import { useCallback, useEffect, useState } from "react";
var useCartItems = (userId, accountId) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchCart = useCallback(async () => {
    if (!userId || !accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getAxios().get(`/api/v1/payments/cart`, {
        params: {
          user_id: userId,
          account_id: accountId
        }
      });
      setCartItems(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, accountId]);
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);
  return { cartItems, loading, error, refetch: fetchCart, setCartItems };
};

// src/hooks/useMarketplaceListingsWithItems.ts
import { useEffect as useEffect2, useState as useState2, useCallback as useCallback2 } from "react";
var useMarketplaceListingsByAccount = (accountId) => {
  const [listings, setListings] = useState2([]);
  const [loading, setLoading] = useState2(false);
  const [error, setError] = useState2(null);
  const fetchListings = useCallback2(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getAxios().get(`/api/v1/marketplace/listings`, {
        params: {
          filter: JSON.stringify({ account_id: accountId }),
          offset: 0,
          limit: 100
        }
      });
      setListings(res.data || []);
    } catch (err) {
      setError(err.message || "Failed to load listings");
    } finally {
      setLoading(false);
    }
  }, [accountId]);
  useEffect2(() => {
    fetchListings();
  }, [fetchListings]);
  return { listings, loading, error, refetch: fetchListings };
};
var useProductsForListing = (listingId) => {
  const [items, setItems] = useState2([]);
  const [loading, setLoading] = useState2(false);
  const [error, setError] = useState2(null);
  const fetchItems = useCallback2(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const linksRes = await getAxios().get(`/api/v1/marketplace/listing_products`, {
        params: {
          filter: JSON.stringify({ listing_id: listingId }),
          offset: 0,
          limit: 1e3
        }
      });
      const productLinks = linksRes.data || [];
      const productIds = productLinks.map((l) => l.product_id);
      if (!productIds.length) {
        setItems([]);
        return;
      }
      const productsRes = await getAxios().get(`/api/v1/marketplace/products`, {
        params: {
          filter: JSON.stringify({
            uuid: { in_: productIds },
            is_deleted: false
            // 🚨 exclude deleted products
          }),
          offset: 0,
          limit: 1e3
        }
      });
      const products = productsRes.data || [];
      const itemIds = products.map((p) => p.item_id);
      const itemsRes = await getAxios().get(`/api/v1/inventory/items`, {
        params: {
          filter: JSON.stringify({ uuid: { in_: itemIds } }),
          offset: 0,
          limit: 1e3
        }
      });
      const allItems = itemsRes.data || [];
      const itemMap = new Map(allItems.map((i) => [i.uuid, i]));
      const matchedItems = products.map((p) => itemMap.get(p.item_id)).filter(Boolean);
      const uniqueItems = Array.from(new Map(matchedItems.map((i) => [i.uuid, i])).values());
      setItems(uniqueItems);
    } catch (err) {
      setError(err.message || "Failed to load listing items");
    } finally {
      setLoading(false);
    }
  }, [listingId]);
  useEffect2(() => {
    fetchItems();
  }, [fetchItems]);
  return { items, loading, error, refetch: fetchItems };
};

// src/hooks/useCartItemsWithDetails.ts
import { useEffect as useEffect3, useState as useState3, useCallback as useCallback3 } from "react";
var useCartItemsWithDetails = (userId, accountId) => {
  const [items, setItems] = useState3([]);
  const [loading, setLoading] = useState3(true);
  const [error, setError] = useState3(null);
  const fetch = useCallback3(async () => {
    if (!userId || !accountId) return;
    try {
      setLoading(true);
      const axios2 = getAxios();
      const cartRes = await axios2.get("/api/v1/payments/cart", {
        params: { user_id: userId, account_id: accountId }
      });
      const cartItems = cartRes.data;
      if (!Array.isArray(cartItems)) {
        setItems([]);
        return;
      }
      const listingIds = cartItems.map((c) => c.listing_id).filter(Boolean);
      const uniqueListingIds = [...new Set(listingIds)];
      const listingsRes = await axios2.get("/api/v1/marketplace/listings", {
        params: {
          filter: JSON.stringify({ uuid: { $in: uniqueListingIds } }),
          offset: 0,
          limit: 100
        }
      });
      const listings = listingsRes.data;
      const listingsById = Object.fromEntries(listings.map((l) => [l.uuid, l]));
      const itemIds = listings.map((l) => l.item_id).filter(Boolean);
      const uniqueItemIds = [...new Set(itemIds)];
      const itemsRes = await axios2.get("/api/v1/inventory/items", {
        params: {
          filter: JSON.stringify({ uuid: { $in: uniqueItemIds } }),
          offset: 0,
          limit: 100
        }
      });
      const inventoryItems = itemsRes.data;
      const itemsById = Object.fromEntries(inventoryItems.map((i) => [i.uuid, i]));
      const merged = cartItems.map((cart) => {
        const listing = listingsById[cart.listing_id] || null;
        const item = listing ? itemsById[listing.item_id] : null;
        return { cart, listing, item };
      });
      setItems(merged);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, accountId]);
  useEffect3(() => {
    fetch();
  }, [fetch]);
  return { items, loading, error, refetch: fetch };
};

// src/hooks/useAddToCart.ts
var useAddToCart = () => {
  const addToCart = async ({
    listing,
    item,
    userId,
    accountId,
    quantity = 1
  }) => {
    return await getAxios().post("/api/v1/payments/cart/add", {
      item: {
        name: item.name || `Listing ${listing.uuid}`,
        user_id: userId,
        account_id: accountId,
        listing_id: listing.uuid,
        description: item.description || "",
        amount: item.sold_for?.toString() || item.cost?.toString() || "1.00",
        quantity,
        is_taxable: listing.is_taxable || false,
        tax_rate: "0.0750",
        tax_amount: "0.00"
      },
      link: {
        source_type: "marketplace",
        source_id: listing.uuid
      }
    });
  };
  return { addToCart };
};

// src/hooks/useCreateListing.ts
import { useState as useState4, useCallback as useCallback4 } from "react";
var endpoint = "/api/v1/marketplace/listings";
var useCreateListing = () => {
  const [loading, setLoading] = useState4(false);
  const [error, setError] = useState4(null);
  const [result, setResult] = useState4(null);
  const create = useCallback4(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAxios().post(endpoint, payload);
      setResult(res.data);
      return res.data;
    } catch (err) {
      setError(err.message || "Failed to create listing.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  return { create, loading, error, result };
};

// src/hooks/useMarketplaceListings.ts
import { useState as useState5, useCallback as useCallback5 } from "react";
var endpoint2 = "/api/v1/marketplace/listings";
var useMarketplaceListings = () => {
  const [listings, setListings] = useState5([]);
  const [loading, setLoading] = useState5(true);
  const [error, setError] = useState5(null);
  const fetchAll = useCallback5(async (filter) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filter) {
        params.set("filter", JSON.stringify(filter));
      }
      params.set("offset", "0");
      params.set("limit", "100");
      const res = await getAxios().get(`${endpoint2}?${params.toString()}`);
      setListings(res.data);
    } catch (err) {
      setError(err.message || "Failed to fetch listings.");
    } finally {
      setLoading(false);
    }
  }, []);
  const create = async (payload) => {
    const res = await getAxios().post(endpoint2, payload);
    await fetchAll();
    return res.data;
  };
  const update = async (uuid, payload) => {
    const res = await getAxios().put(`${endpoint2}/${uuid}`, payload);
    await fetchAll();
    return res.data;
  };
  const patch = async (uuid, payload) => {
    const res = await getAxios().patch(`${endpoint2}/${uuid}`, { ...payload, uuid });
    await fetchAll();
    return res.data;
  };
  const remove = async (uuid) => {
    const res = await getAxios().delete(`${endpoint2}/${uuid}`);
    await fetchAll();
    return res.data;
  };
  return { listings, loading, error, fetchAll, create, update, patch, remove };
};

// src/hooks/useMarketplaceProducts.ts
import { useState as useState6, useCallback as useCallback6 } from "react";
var endpoint3 = "/api/v1/marketplace/products";
var useMarketplaceProducts = () => {
  const [products, setProducts] = useState6([]);
  const [loading, setLoading] = useState6(false);
  const [error, setError] = useState6(null);
  const fetchAll = useCallback6(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAxios().get(endpoint3);
      setProducts(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch products.");
    } finally {
      setLoading(false);
    }
  }, []);
  const create = useCallback6(async (item_id, name) => {
    try {
      const payload = { item_id, name };
      const response = await getAxios().post(endpoint3, payload);
      await fetchAll();
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to create product.");
      throw err;
    }
  }, [fetchAll]);
  return {
    products,
    loading,
    error,
    fetchAll,
    create
  };
};

// src/hooks/useMarketplaceListingProducts.ts
import { useState as useState7, useCallback as useCallback7 } from "react";
var useMarketplaceListingProducts = (listingId) => {
  const [products, setProducts] = useState7([]);
  const [loading, setLoading] = useState7(false);
  const [error, setError] = useState7(null);
  const fetchProducts = useCallback7(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAxios().get(`/api/v1/marketplace/listing/${listingId}/products`);
      setProducts(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch linked products.");
    } finally {
      setLoading(false);
    }
  }, [listingId]);
  const addProduct = useCallback7(async (productId) => {
    await getAxios().post(`/api/v1/marketplace/listing/${listingId}/products/${productId}`);
    await fetchProducts();
  }, [listingId, fetchProducts]);
  const removeProduct = useCallback7(async (productId) => {
    await getAxios().delete(`/api/v1/marketplace/listing/${listingId}/products/${productId}`);
    await getAxios().delete(`/api/v1/marketplace/products/${productId}`);
    await fetchProducts();
  }, [listingId, fetchProducts]);
  return {
    data: products,
    loading,
    error,
    fetchAll: fetchProducts,
    add: addProduct,
    remove: removeProduct
  };
};

// src/hooks/useListingProducts.ts
import { useState as useState8, useCallback as useCallback8 } from "react";
var endpoint4 = "/api/v1/marketplace/listing_products";
var useListingProducts = (listingId) => {
  const [loading, setLoading] = useState8(false);
  const [error, setError] = useState8(null);
  const [data, setData] = useState8([]);
  const fetchAll = useCallback8(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getAxios().get(`${endpoint4}?listing_id=${listingId}`);
      setData(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch listing products.");
    } finally {
      setLoading(false);
    }
  }, [listingId]);
  const add = useCallback8(async (product_id) => {
    try {
      const payload = { listing_id: listingId, product_id };
      const response = await getAxios().post(endpoint4, payload);
      await fetchAll();
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to add listing product.");
      throw err;
    }
  }, [fetchAll, listingId]);
  const remove = useCallback8(async (product_id) => {
    try {
      await getAxios().delete(endpoint4, {
        data: { listing_id: listingId, product_id }
      });
      await fetchAll();
    } catch (err) {
      setError(err.message || "Failed to remove listing product.");
      throw err;
    }
  }, [fetchAll, listingId]);
  return {
    data,
    loading,
    error,
    fetchAll,
    add,
    remove
  };
};
export {
  createTransaction,
  getAxios,
  setApiBaseUrl,
  useAddToCart,
  useCartItems,
  useCartItemsWithDetails,
  useCreateCart,
  useCreateListing,
  useCreateTransaction,
  useListingProducts,
  useMarketplaceListingProducts,
  useMarketplaceListings,
  useMarketplaceListingsByAccount,
  useMarketplaceProducts,
  useProductsForListing
};
